import { get } from 'global/lib/lodash';
import { getSource, checkIfVideo } from 'global/components/Media';
import { findOne, getImageNameFromSrc } from 'global/utils';

/** Hamming distance thresholds for 64-bit pHash duplicates. */
const RELATED_DISTANCE_THRESHOLD = 15;
const UNRELATED_DISTANCE_THRESHOLD = 8;
const MIN_RELATED_CAPTION_LENGTH = 10;

const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/tiff', 'image/bmp'];
const EXT_TO_MIME = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  bmp: 'image/bmp',
};

const IMAGE_URL_KEYS = ['thumbUrl', 'thumb-url', 'thumb', 'largeUrl', 'large-url', 'large', 'fullUrl', 'full', 'url'];
const FILENAME_KEYS = ['fileName', 'file_name', 'name', 'filename'];
const CACHE_SRC_KEYS = ['thumbUrl', 'thumb-url', 'thumb', 'fullUrl', 'url'];

const hashCache = new Map();
const hashInflight = new Map();
let pHashLoadPromise = null;

const normalizeExt = (ext = '') => {
  const e = String(ext).toLowerCase();
  if (e === 'jpeg') return 'jpg';
  if (e === 'tiff') return 'tif';
  return e;
};

const getFilename = (img) => {
  const raw = findOne(img, FILENAME_KEYS);
  if (raw) return getImageNameFromSrc(raw).toLowerCase();

  const src = getSource(img, ['fullUrl', 'full', 'largeUrl', 'large-url', 'large', 'thumbUrl', 'thumb-url', 'thumb', 'url']);
  return src ? getImageNameFromSrc(src).toLowerCase() : '';
};

const getCaption = (img) => {
  const raw = get(img, 'custom_properties.caption', get(img, 'customProperties.caption', ''));
  return raw ? String(raw).trim() : '';
};

/** Normalize extensions so jpg/jpeg and tif/tiff compare together. */
export const getPhotoExtension = (img) => {
  const filename = getFilename(img);
  if (!filename || !filename.includes('.')) return '';
  return normalizeExt(filename.split('.').pop());
};

const getPhotoId = (img, fallback) => {
  const id = get(img, 'id');
  return id != null ? String(id) : String(fallback);
};

const loadPHash = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('pHash requires a browser'));
  if (window.pHash) return Promise.resolve(window.pHash);
  if (pHashLoadPromise) return pHashLoadPromise;

  pHashLoadPromise = new Promise((resolve, reject) => {
    const fail = () => {
      pHashLoadPromise = null;
      reject(new Error('Failed to load phash-js'));
    };
    const ready = () => (window.pHash ? resolve(window.pHash) : fail());

    const existing = document.querySelector('script[data-phash-js]');
    if (existing) {
      if (window.pHash) return ready();
      existing.addEventListener('load', ready, { once: true });
      existing.addEventListener('error', fail, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL || ''}/phash/phash.js`;
    script.async = true;
    script.dataset.phashJs = 'true';
    script.onload = ready;
    script.onerror = fail;
    document.head.appendChild(script);
  });

  return pHashLoadPromise;
};

const getImageUrls = (img) => {
  const urls = [];
  const seen = new Set();
  for (let i = 0; i < IMAGE_URL_KEYS.length; i++) {
    const value = get(img, IMAGE_URL_KEYS[i]);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    urls.push(value);
  }
  return urls;
};

const fetchImageAsFile = async (img) => {
  const urls = getImageUrls(img).filter((url) => {
    if (!url || String(url).startsWith('data:')) return false;
    if (checkIfVideo(url)) return false;
    return true;
  });
  if (!urls.length) {
    throw new Error('No image URL available');
  }

  const attempts = [];
  for (let i = 0; i < urls.length; i++) {
    const url = urls[i];
    try {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) {
        attempts.push(`${i}: HTTP ${response.status}`);
        continue;
      }

      const blob = await response.blob();
      const filename = getFilename(img) || 'image.jpg';
      // Prefer the fetched URL's extension for MIME — thumbs are often .jpg even when the original is .png.
      const urlExt = normalizeExt(String(url).split('?')[0].split('.').pop());
      const type =
        (SUPPORTED_TYPES.includes(blob.type) && blob.type) ||
        EXT_TO_MIME[urlExt] ||
        EXT_TO_MIME[getPhotoExtension(img)] ||
        '';
      if (!SUPPORTED_TYPES.includes(type)) {
        attempts.push(`${i}: unsupported MIME blob.type="${blob.type || ''}" resolved="${type}"`);
        continue;
      }

      return { file: new File([blob], filename, { type }), url, blobType: blob.type || '', resolvedType: type };
    } catch (err) {
      // Bare "Failed to fetch" usually means the browser blocked reading the response (often CORS on an S3 error).
      attempts.push(`${i}: ${err?.message || String(err)}`);
    }
  }

  throw new Error(`All image URL fetches failed (${attempts.join('; ')})`);
};

/** @returns {{ hash: string|null, error: string|null }} */
const getPhotoHash = async (img) => {
  const key = get(img, 'id') != null ? `id:${img.id}` : `src:${getSource(img, CACHE_SRC_KEYS)}`;
  if (!key || key === 'src:undefined') {
    return { hash: null, error: 'No cache key (missing id/url)' };
  }
  if (hashCache.has(key)) return hashCache.get(key);
  if (hashInflight.has(key)) return hashInflight.get(key);

  const work = (async () => {
    try {
      const pHash = await loadPHash();
      const { file, url, blobType, resolvedType } = await fetchImageAsFile(img);
      const hash = await pHash.hash(file);
      const value = hash?.value || hash?.toBinary?.() || null;
      if (!value) {
        const result = { hash: null, error: `pHash returned empty value (url=${url}, blobType=${blobType}, resolvedType=${resolvedType})` };
        hashCache.set(key, result);
        return result;
      }
      const result = { hash: value, error: null };
      hashCache.set(key, result);
      return result;
    } catch (err) {
      const result = { hash: null, error: err?.message || String(err) };
      hashCache.set(key, result);
      return result;
    } finally {
      hashInflight.delete(key);
    }
  })();

  hashInflight.set(key, work);
  return work;
};

const hammingDistance = (a, b) => {
  if (!a || !b) return Number.POSITIVE_INFINITY;
  if (a.length === b.length) {
    let distance = 0;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) distance += 1;
    }
    return distance;
  }
  const length = Math.max(a.length, b.length);
  const left = a.padStart(length, '0');
  const right = b.padStart(length, '0');
  let distance = 0;
  for (let i = 0; i < length; i++) {
    if (left[i] !== right[i]) distance += 1;
  }
  return distance;
};

const isRelatedMetadata = (a, b) => {
  if (a.filename && b.filename && a.filename === b.filename) return true;
  if (
    a.caption &&
    b.caption &&
    a.caption.length > MIN_RELATED_CAPTION_LENGTH &&
    a.caption === b.caption
  ) {
    return true;
  }
  return false;
};

const distanceThresholdForPair = (a, b, opts = {}) => {
  const related = opts.relatedThreshold ?? RELATED_DISTANCE_THRESHOLD;
  const unrelated = opts.unrelatedThreshold ?? UNRELATED_DISTANCE_THRESHOLD;
  return isRelatedMetadata(a, b) ? related : unrelated;
};

const isDuplicateHash = (prior, hash, meta, opts = {}) => {
  for (let i = 0; i < prior.length; i++) {
    const entry = prior[i];
    const threshold = distanceThresholdForPair(meta, entry, opts);
    if (hammingDistance(entry.hash, hash) <= threshold) return true;
  }
  return false;
};

/**
 * Keep photos that are not pHash-duplicates of `existing` or of earlier items
 * in `candidates`. Comparison is limited to the same file extension.
 * Same-filename or same long-caption pairs use a looser Hamming threshold.
 * Does not mutate `existing` (safe for append-only pagination).
 */
const keepUniqueByExtension = async (candidates = [], existing = [], opts = {}) => {
  if (!candidates.length) return [];

  const seenIds = new Set(existing.map((img, i) => getPhotoId(img, i)));
  const hashesByExt = new Map();

  // Hash existing + candidates in parallel; uniqueness pass stays order-preserving.
  const [existingResults, candidateResults] = await Promise.all([
    Promise.all(
      existing.map(async (img) => {
        const ext = getPhotoExtension(img);
        if (!ext) return null;
        const { hash } = await getPhotoHash(img);
        return hash
          ? { ext, hash, filename: getFilename(img), caption: getCaption(img) }
          : null;
      })
    ),
    Promise.all(candidates.map((img) => getPhotoHash(img))),
  ]);

  for (let i = 0; i < existingResults.length; i++) {
    const row = existingResults[i];
    if (!row) continue;
    if (!hashesByExt.has(row.ext)) hashesByExt.set(row.ext, []);
    hashesByExt.get(row.ext).push({ hash: row.hash, filename: row.filename, caption: row.caption });
  }

  const kept = [];

  for (let i = 0; i < candidates.length; i++) {
    const img = candidates[i];
    const id = getPhotoId(img, `new_${i}`);
    if (seenIds.has(id)) continue;

    const ext = getPhotoExtension(img);
    if (!ext) {
      kept.push(img);
      seenIds.add(id);
      continue;
    }

    const { hash } = candidateResults[i];
    const meta = { filename: getFilename(img), caption: getCaption(img) };
    const prior = hashesByExt.get(ext) || [];
    if (hash && isDuplicateHash(prior, hash, meta, opts)) continue;

    kept.push(img);
    seenIds.add(id);
    if (hash) {
      if (!hashesByExt.has(ext)) hashesByExt.set(ext, []);
      hashesByExt.get(ext).push({ hash, ...meta });
    }
  }

  return kept;
};

/** Full-list dedupe for the first page / new search. */
export const dedupePhotosByFilenameAndHash = (images = [], opts = {}) =>
  keepUniqueByExtension(images, [], opts);

/** Append-only: return non-duplicates from a new page compared to what's already shown. */
export const filterNewPhotosAgainstExisting = (candidates = [], existing = [], opts = {}) =>
  keepUniqueByExtension(candidates, existing, opts);
